import { useQuery } from '@tanstack/react-query'
import { durationToMs } from '@bloo-team/api-client'
import type { PostureStats, PostureTopEntry } from '@bloo-team/api-client'
import { isDemoSession } from '@bloo-team/core-shell'
import { SEVERITY_ORDER, readSignals, type Signal, type SignalSeverity } from './signal-model'
import { demoCasesApi, demoSignalRowsCached, liveCasesApi, type DemoCase, type SignalWindow } from './signals'

/**
 * Vantage · Overview — posture data (docs/IA.md IA8).
 *
 * Live mode hits the legacy "signal statistics" endpoint `POST
 * /api/cases/stats` (VERIFIED, docs/inventory/signals-capture.json —
 * DISTINCT from the already-wired `case_stats`). Demo mode derives the same
 * shape from the shared signal fixture + the in-memory case store, so every
 * number on the overview agrees with what the Signals and Cases pages show.
 */

function pctDiff(cur: number, prev: number): number | null {
  if (prev <= 0) return null
  return Math.round(((cur - prev) / prev) * 1000) / 10
}

function tally(values: Iterable<string>): Map<string, number> {
  const m = new Map<string, number>()
  for (const v of values) if (v) m.set(v, (m.get(v) ?? 0) + 1)
  return m
}

/** Count-desc (name asc on ties), top N — the `top_*.stats` convention. */
function top(m: Map<string, number>, n = 10): PostureTopEntry[] {
  return [...m.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, n)
    .map(([name, count]) => ({ name, count }))
}

function entityValues(signals: Signal[], role: 'Suspect' | 'Target'): string[] {
  return signals.flatMap((s) => s.entities.filter((e) => e.role === role).map((e) => e.value))
}

/**
 * The demo `cases/stats` reply, replayed over the fixture: counts per
 * severity / stream / tactic / detection / suspect / target all come from
 * the SAME window-filtered signals, and `percent_diff` compares against the
 * preceding window of equal length — so the tiles stay internally
 * consistent (severity splits sum to the signal count, no top list
 * outnumbers the window). MTTD is the mean of the seeded cases' TTA.
 */
export function demoPostureStats(all: Signal[], cases: DemoCase[], window: SignalWindow, now = Date.now()): PostureStats {
  const span = durationToMs(window) ?? 7 * 86_400_000
  const since = now - span
  const cur = all.filter((s) => s.at >= since && s.at <= now)
  const prev = all.filter((s) => s.at >= since - span && s.at < since)

  const counter = (c: number, p: number) => ({ count: c, percentDiff: pctDiff(c, p) })
  const uniq = (xs: string[]) => new Set(xs.filter(Boolean)).size

  const detections = tally(cur.map((s) => s.detectionName))
  const suspects = tally(entityValues(cur, 'Suspect'))
  const targets = tally(entityValues(cur, 'Target'))
  const bySeverity = tally(cur.map((s) => s.severity))
  const sevIndex = (sev: string) => {
    const i = SEVERITY_ORDER.indexOf(sev as SignalSeverity)
    return i === -1 ? SEVERITY_ORDER.length : i
  }

  const casesCur = cases.filter((c) => c.createdAt >= since && c.createdAt <= now).length
  const casesPrev = cases.filter((c) => c.createdAt >= since - span && c.createdAt < since).length
  const ttas = cases.map((c) => c.tta).filter((t): t is number => t !== null)

  return {
    cases: counter(casesCur, casesPrev),
    signals: counter(cur.length, prev.length),
    detections: counter(detections.size, uniq(prev.map((s) => s.detectionName))),
    suspects: counter(suspects.size, uniq(entityValues(prev, 'Suspect'))),
    targets: counter(targets.size, uniq(entityValues(prev, 'Target'))),
    mttdSeconds: ttas.length ? Math.round(ttas.reduce((s, t) => s + t, 0) / ttas.length) : null,
    severity: [...bySeverity.entries()]
      .sort((a, b) => sevIndex(a[0]) - sevIndex(b[0]))
      .map(([severity, value]) => ({ severity, value })),
    streams: top(tally(cur.flatMap((s) => s.streams))).map((t) => ({ stream: t.name, value: t.count })),
    streamNames: [...new Set(cur.flatMap((s) => s.streams))].sort((a, b) => a.localeCompare(b)),
    topDetections: top(detections),
    // The fixture's "workbook" IS the detection — the live field is the
    // workbook name behind each detection.
    topSources: top(detections),
    topSuspects: top(suspects),
    topTargets: top(targets),
    topTactics: top(tally(cur.map((s) => s.tactic))),
    topTechniques: top(tally(cur.map((s) => s.technique))),
  }
}

export interface OverviewScope {
  clusterId: string
  scopeId: string
}

/**
 * Posture counters + top lists for the selected window. Refetches on window
 * change only (the window is part of the query key). Read-only — safe for
 * the Viewer role that can see the Overview.
 */
export function usePostureStats(q: OverviewScope, window: SignalWindow) {
  const demo = isDemoSession()
  return useQuery<PostureStats>({
    queryKey: ['posture', q.clusterId, q.scopeId, window, demo ? 'demo' : 'live'],
    queryFn: async () =>
      demo
        ? demoPostureStats(readSignals(demoSignalRowsCached()), await demoCasesApi.list(), window)
        : liveCasesApi.postureStats({ ...q, duration: window }),
    staleTime: 60_000,
    retry: 1,
  })
}

/** '—' for null; otherwise a coarse humanized duration from SECONDS. */
export function humanizeSecs(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—'
  if (s < 90) return `${Math.max(1, Math.round(s))}s`
  const m = Math.round(s / 60)
  if (m < 90) return `${m}m`
  const h = Math.round(s / 3600)
  if (h < 48) return `${h}h`
  return `${Math.round(s / 86_400)}d`
}
